import { Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import PropTypes from "prop-types";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";

// Local Imports
import { Button } from "../Button";

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  className,
  blur = false,
  showCloseButton = true,
  ...rest
}) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-100 flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={onClose}
        {...rest}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={clsx(
              "absolute inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40",
              blur && "backdrop-blur-sm"
            )}
          />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <DialogPanel
            className={clsx(
              "scrollbar-sm relative flex w-full flex-col overflow-y-auto rounded-xl bg-white text-left transition-all dark:bg-dark-700 shadow-xl print:shadow-none",
              sizeClasses[size],
              className
            )}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4 dark:border-dark-500 sm:px-5">
                {title && (
                  <DialogTitle
                    as="h3"
                    className="text-lg font-bold text-gray-800 dark:text-dark-100"
                  >
                    {title}
                  </DialogTitle>
                )}
                {showCloseButton && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="size-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:text-dark-300 dark:hover:bg-dark-600 dark:hover:text-dark-100"
                  >
                    <XMarkIcon className="size-5" />
                  </Button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-4 sm:p-5">{children}</div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  size: PropTypes.oneOf(Object.keys(sizeClasses)),
  className: PropTypes.string,
  blur: PropTypes.bool,
  showCloseButton: PropTypes.bool,
};

Modal.displayName = "Modal";